import React, { useEffect, useState } from 'react';
import { buildScopedApiUrl, getApiHeaders } from './config';

const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const money = value => `${value < 0 ? '−' : value > 0 ? '+' : ''}$${Math.abs(value).toFixed(2)}`;

export default function MobileTradingDesk({ workspace, canViewPositions, onPositions, onSetup, onSignals }) {
  const [state, setState] = useState({ loading: true, rows: [], error: '' });
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!canViewPositions) return;
    const controller = new AbortController();
    setState({ loading: true, rows: [], error: '' });
    fetch(buildScopedApiUrl('/api/bot/ui/get/allOpenPositionsAlternative', workspace), {
      headers: getApiHeaders(), signal: controller.signal,
    }).then(response => {
      if (!response.ok) throw new Error('Positions are unavailable.');
      return response.json();
    }).then(rows => {
      if (!Array.isArray(rows)) throw new Error('Positions are unavailable.');
      if (!controller.signal.aborted) setState({ loading: false, rows, error: '' });
    }).catch(error => {
      if (!controller.signal.aborted) setState({ loading: false, rows: [], error: 'Unable to load positions. Please retry.' });
    });
    return () => controller.abort();
  }, [workspace, canViewPositions, refresh]);
  const pnl = state.rows.reduce((sum, row) => sum + number(row.unrealizedPL), 0);
  const profits = state.rows.filter(row => number(row.unrealizedPL) > 0).length;
  const losses = state.rows.filter(row => number(row.unrealizedPL) < 0).length;
  return <section className="mobile-trading-desk" aria-label="Trading desk">
    <div className="mobile-desk-heading"><div><p className="mobile-eyebrow">YOUR WORKSPACE</p><h2>Trading desk</h2></div><span className="mobile-mode">{canViewPositions ? 'Account overview' : 'Signals workspace'}</span></div>
    {canViewPositions && <>
      <div className="mobile-pnl-card" aria-live="polite">
        <div className="mobile-desk-heading"><span>Unrealized P/L</span><button className="mobile-text-button" disabled={state.loading} onClick={() => setRefresh(value => value + 1)}>Refresh</button></div>
        {state.loading ? <p className="mobile-desk-notice">Loading positions…</p> : state.error ? <p className="mobile-desk-notice" role="alert">{state.error}</p> : <>
          <strong className={`mobile-pnl-value ${pnl < 0 ? 'is-negative' : pnl > 0 ? 'is-positive' : ''}`}>{money(pnl)}</strong>
          <div className="mobile-desk-stats"><div><strong>{state.rows.length}</strong><span>Open positions</span></div><div><strong>{profits} / {losses}</strong><span>In profit / loss</span></div></div>
        </>}
      </div>
      <div><div className="mobile-desk-heading"><h3>Positions</h3><button className="mobile-text-button" onClick={onPositions}>View all →</button></div>
        {!state.loading && !state.error && state.rows.length === 0 && <p className="mobile-desk-notice">No open positions in this workspace.</p>}
        {!state.loading && !state.error && state.rows.slice(0, 3).map((row, index) => <button className="mobile-position-preview" key={`${row.symbol}-${row.holdSide}-${index}`} onClick={onPositions}>
          <span className="mobile-symbol-icon" aria-hidden="true">{String(row.symbol || '?').slice(0, 1)}</span><span className="mobile-position-name"><strong>{row.symbol}</strong><span>{row.holdSide} · {row.leverage}× · ${number(row.margin).toFixed(2)} margin</span></span><strong className={number(row.unrealizedPL) < 0 ? 'is-negative' : number(row.unrealizedPL) > 0 ? 'is-positive' : ''}>{money(number(row.unrealizedPL))}</strong>
        </button>)}
      </div>
    </>}
    <div className="mobile-desk-shortcuts">
      {canViewPositions && <button onClick={onSetup}><span aria-hidden="true">◇</span><strong>Trade setup</strong><small>Configure entry & exits</small></button>}
      <button onClick={onSignals}><span aria-hidden="true">⌁</span><strong>Trading signals</strong><small>Explore market conditions</small></button>
    </div>
    <p className="mobile-desk-footnote">{workspace.exchange} / {workspace.marketType} · {canViewPositions ? 'Position snapshot · Refresh for latest data' : 'Market signals and analytics'}</p>
  </section>;
}
